import React from "react";
import { motion } from "framer-motion";
import {
  PhoneCall,
  EnvelopeSimple,
  MapPin,
  ArrowUpRight,
  Clock,
  ShieldCheck,
  Lightning,
} from "@phosphor-icons/react";
import { COMPANY } from "@/lib/siteData";

const ITEMS = [
  {
    number: "01",
    icon: PhoneCall,
    label: "Call Us Directly",
    value: COMPANY.phone,
    sub: "Mon – Sat · 7am — 6pm",
    note: "Talk to a real estimator, not a call center",
    href: `tel:${COMPANY.phoneRaw}`,
    testid: "contact-phone",
    external: false,
  },
  {
    number: "02",
    icon: EnvelopeSimple,
    label: "Email Our Team",
    value: COMPANY.email,
    sub: "Replies within 24 hours",
    note: "Send photos, plans, or project details",
    href: `mailto:${COMPANY.email}`,
    testid: "contact-email",
    external: false,
  },
  {
    number: "03",
    icon: MapPin,
    label: "Visit The Shop",
    value: COMPANY.address,
    sub: COMPANY.city,
    note: "Drop by — coffee's on us",
    href: "https://www.google.com/maps/search/?api=1&query=116-2723+Faithful+Avenue+Saskatoon+SK",
    testid: "contact-address",
    external: true,
  },
];

const TRUST_SIGNALS = [
  { icon: Clock, label: "Response < 24h" },
  { icon: ShieldCheck, label: "Licensed & Insured" },
  { icon: Lightning, label: "Free Site Assessment" },
];

export const Contact = () => {
  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-[#121212] grain py-20 md:py-28 overflow-hidden"
    >
      {/* Decorative background hairlines */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.07] pointer-events-none z-0"
        style={{
          backgroundImage:
            "linear-gradient(#c41e3a 1px, transparent 1px), linear-gradient(90deg, #c41e3a 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />

      <div className="max-w-[1400px] mx-auto px-5 md:px-10 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left headline */}
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-10 h-[2px] bg-[#c41e3a]" />
              <span className="font-mono-upper text-[11px] text-[#c41e3a]">
                Start Your Project
              </span>
            </div>
            <h2 className="font-display font-black text-white text-4xl md:text-6xl leading-[1] tracking-[-0.03em]">
              Ready for a
              <span className="block text-[#c41e3a]">roof that lasts?</span>
            </h2>
            <p className="font-body text-white/70 text-base md:text-lg mt-6 leading-relaxed max-w-md">
              Call, email, or stop by the shop. We&apos;ll come out, walk your
              roof, and give you a straight-up quote — no pressure, no sales
              games.
            </p>

            {/* Trust signals row */}
            <ul className="mt-8 flex flex-wrap gap-x-6 gap-y-3">
              {TRUST_SIGNALS.map((t) => {
                const Icon = t.icon;
                return (
                  <li
                    key={t.label}
                    className="inline-flex items-center gap-2 font-mono-upper text-[10px] text-white/70"
                  >
                    <Icon size={14} weight="fill" className="text-[#c41e3a]" />
                    {t.label}
                  </li>
                );
              })}
            </ul>

            <a
              href={`tel:${COMPANY.phoneRaw}`}
              data-testid="contact-cta-call"
              className="group mt-10 inline-flex items-center gap-3 bg-[#c41e3a] hover:bg-[#e62244] text-white font-bold tracking-[0.22em] uppercase text-[11px] px-6 py-4 transition-colors"
            >
              Call {COMPANY.phone}
              <ArrowUpRight
                size={16}
                weight="bold"
                className="transition-transform group-hover:-translate-y-[2px] group-hover:translate-x-[2px]"
              />
            </a>
          </div>

          {/* Premium contact cards — horizontal stacked */}
          <div className="lg:col-span-7 flex flex-col gap-5">
            {ITEMS.map((it, idx) => {
              const Icon = it.icon;
              return (
                <motion.a
                  key={it.label}
                  href={it.href}
                  target={it.external ? "_blank" : undefined}
                  rel={it.external ? "noopener noreferrer" : undefined}
                  initial={{ opacity: 0, y: 28 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.6, delay: idx * 0.12 }}
                  data-testid={it.testid}
                  className="group relative flex items-stretch bg-[#181818] hover:bg-[#1e1e1e] border border-white/10 hover:border-[#c41e3a]/70 transition-all duration-300 overflow-hidden"
                >
                  {/* Index column */}
                  <div className="relative flex items-center justify-center w-20 md:w-28 shrink-0 border-r border-white/10 bg-gradient-to-b from-[#1a1a1a] to-[#141414]">
                    {/* Red wedge that slides in on hover */}
                    <span className="absolute inset-0 bg-[#c41e3a] -translate-x-full group-hover:translate-x-0 transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]" />
                    <span className="relative font-display font-black text-[#c41e3a] group-hover:text-white text-4xl md:text-5xl tracking-tighter transition-colors duration-300">
                      {it.number}
                    </span>
                    {/* Vertical label */}
                    <span
                      style={{ writingMode: "vertical-rl" }}
                      className="absolute bottom-3 right-2 transform rotate-180 font-mono-upper text-[8px] text-white/30 group-hover:text-white/70 transition-colors"
                    >
                      Step
                    </span>
                  </div>

                  {/* Content */}
                  <div className="flex-1 flex items-center gap-5 md:gap-7 px-5 md:px-8 py-6 md:py-7 min-w-0">
                    {/* Icon */}
                    <div className="hidden sm:inline-flex items-center justify-center w-14 h-14 shrink-0 border border-white/15 group-hover:border-[#c41e3a] group-hover:bg-[#c41e3a]/10 transition-colors">
                      <Icon size={22} weight="duotone" className="text-[#c41e3a]" />
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="font-mono-upper text-[10px] text-[#c41e3a]">
                        {it.label}
                      </div>
                      <div className="font-display font-bold text-white text-[14px] sm:text-base md:text-lg lg:text-xl mt-1 leading-[1.25] tracking-[-0.01em] [word-break:break-word] group-hover:text-[#c41e3a] transition-colors">
                        {it.value}
                      </div>
                      <div className="mt-2 flex flex-col sm:flex-row sm:items-center sm:gap-4 text-[12px]">
                        <span className="font-body text-white/70">{it.sub}</span>
                        <span className="hidden sm:block w-1 h-1 rounded-full bg-[#c41e3a]/70" />
                        <span className="font-body text-white/45 italic">
                          {it.note}
                        </span>
                      </div>
                    </div>

                    {/* Arrow */}
                    <div className="shrink-0 w-12 h-12 md:w-14 md:h-14 inline-flex items-center justify-center border border-white/10 group-hover:bg-[#c41e3a] group-hover:border-[#c41e3a] transition-colors">
                      <ArrowUpRight
                        size={20}
                        weight="bold"
                        className="text-white/80 group-hover:text-white transition-all duration-300 group-hover:-translate-y-[2px] group-hover:translate-x-[2px]"
                      />
                    </div>
                  </div>

                  {/* Bottom red sweep */}
                  <span className="absolute left-0 bottom-0 h-[3px] w-0 bg-[#c41e3a] group-hover:w-full transition-all duration-500" />
                </motion.a>
              );
            })}

            {/* Decorative footer note */}
            <div className="flex items-center gap-3 mt-2 pl-1">
              <span className="w-6 h-[2px] bg-[#c41e3a]" />
              <span className="font-mono-upper text-[10px] text-white/50">
                Serving Saskatoon & all of Saskatchewan
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
